import React, { useMemo, useState } from 'react'
import { AnimatePresence } from 'framer-motion'
import type { Priority } from '../../redux/tasks'
import FocusAddTaskInput from '../FocusAddTaskInput/FocusAddTaskInput'
import FocusTaskCard from '../FocusTaskCard/FocusTaskCard'
import PomodoroModal from '../PomodoroModal/PomodoroModal'
import SectionHeader from '../SectionHeader/SectionHeader'
import TaskSortMenu, {
  DEFAULT_TASK_SORT,
  type TaskSortMode,
} from '../TaskSortMenu/TaskSortMenu'
import { SkeletonTaskList } from '../Skeleton/Skeleton'
import styles from './FocusSection.module.css'

export type { TaskSortMode }
export { DEFAULT_TASK_SORT }

export type FocusTask = {
  id: string
  title: string
  metaLine?: string
  completed?: boolean
  priority?: Priority
}

export type FocusSectionProps = {
  title?: string
  tasks: FocusTask[]
  /**
   * When this value changes, the task list remounts as a whole so Framer Motion
   * does not run exit animations for the previous list (e.g. switching days).
   */
  tasksScopeKey?: string
  /** When true, shows placeholder rows instead of task cards (initial load). */
  loading?: boolean
  taskSort?: TaskSortMode
  onTaskSortChange?: (mode: TaskSortMode) => void
  onAddTask?: (label: string) => void
  onToggleTask?: (id: string) => void
  onViewTask?: (id: string) => void
  onDeleteTask?: (id: string) => void
}

const remainingLabel = (count: number) => {
  if (count === 1) return '1 task remaining'
  return `${count} tasks remaining`
}

const FocusSection = ({
  title = 'On your list',
  tasks,
  tasksScopeKey,
  loading = false,
  taskSort = DEFAULT_TASK_SORT,
  onTaskSortChange,
  onAddTask,
  onToggleTask,
  onViewTask,
  onDeleteTask,
}: FocusSectionProps) => {
  const remaining = useMemo(
    () => tasks.reduce((n, t) => n + (t.completed ? 0 : 1), 0),
    [tasks]
  )
  const [pomodoroTask, setPomodoroTask] = useState<FocusTask | null>(null)

  return (
    <section className={styles.section} aria-labelledby="tasks-heading">
      <SectionHeader title={title} titleId="tasks-heading">
        <div className={styles.headerMeta}>
          <p className={styles.counter}>{remainingLabel(remaining)}</p>
          {onTaskSortChange ? (
            <TaskSortMenu value={taskSort} onChange={onTaskSortChange} />
          ) : null}
        </div>
      </SectionHeader>
      <div className={styles.list}>
        {loading ? (
          <SkeletonTaskList count={4} />
        ) : (
          <div
            className={styles.listTasks}
            key={tasksScopeKey ?? 'tasks'}
          >
            <AnimatePresence initial={false} mode="popLayout">
              {tasks.map((task) => (
                <FocusTaskCard
                  key={task.id}
                  title={task.title}
                  metaLine={task.metaLine}
                  completed={!!task.completed}
                  priority={task.priority}
                  onToggle={() => onToggleTask?.(task.id)}
                  onPomodoro={() => setPomodoroTask(task)}
                  onViewTask={onViewTask ? () => onViewTask(task.id) : undefined}
                  onDelete={onDeleteTask ? () => onDeleteTask(task.id) : undefined}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
        <FocusAddTaskInput onSubmit={onAddTask} />
      </div>
      <PomodoroModal
        key={pomodoroTask?.id ?? 'closed'}
        isOpen={!!pomodoroTask}
        taskTitle={pomodoroTask?.title ?? ''}
        onClose={() => setPomodoroTask(null)}
      />
    </section>
  )
}

export default FocusSection
